import { ActionIcon, Button, Group, Modal, Stack, Text, Tooltip } from '@mantine/core'
import { IconDownload, IconZoomIn, IconZoomOut, IconZoomReset } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'

type ImageZoomModalProps = {
  opened: boolean
  imageUrl: string | null
  onClose: () => void
  title?: string
  subtitle?: string
  imageAlt?: string
  downloadLabel?: string
  downloadLoading?: boolean
  downloadDisabled?: boolean
  onDownload?: () => void
}

export function ImageZoomModal({
  opened,
  imageUrl,
  onClose,
  title = 'Image Preview',
  subtitle,
  imageAlt = 'Preview',
  downloadLabel = 'Download',
  downloadLoading = false,
  downloadDisabled = false,
  onDownload,
}: ImageZoomModalProps) {
  const [zoomScale, setZoomScale] = useState(1)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setZoomScale(1)
  }, [opened, imageUrl])

  useEffect(() => {
    const node = scrollContainerRef.current
    if (!node) {
      return
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()
      setZoomScale((current) => {
        const delta = event.deltaY < 0 ? 0.1 : -0.1
        return Math.min(5, Math.max(0.5, Math.round((current + delta) * 10) / 10))
      })
    }

    node.addEventListener('wheel', handleWheel, { passive: false })
    return () => node.removeEventListener('wheel', handleWheel)
  }, [opened, imageUrl])

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="80rem"
      centered
      radius="md"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      {imageUrl && (
        <Stack gap="md">
          <Group
            justify="space-between"
            align="center"
            className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
          >
            <div className="min-w-0">
              <Text size="sm" fw={600}>
                Preview
              </Text>
              {subtitle && (
                <Text size="xs" c="dimmed">
                  {subtitle}
                </Text>
              )}
            </div>
            <Group gap="xs">
              <Group gap={4}>
                <Tooltip label="Zoom out">
                  <ActionIcon
                    variant="default"
                    aria-label="Zoom out"
                    onClick={() => setZoomScale((current) => Math.max(0.5, current - 0.25))}
                  >
                    <IconZoomOut size={16} />
                  </ActionIcon>
                </Tooltip>
                <Text size="sm" w={52} ta="center">
                  {Math.round(zoomScale * 100)}%
                </Text>
                <Tooltip label="Zoom in">
                  <ActionIcon
                    variant="default"
                    aria-label="Zoom in"
                    onClick={() => setZoomScale((current) => Math.min(5, current + 0.25))}
                  >
                    <IconZoomIn size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Reset zoom">
                  <ActionIcon
                    variant="default"
                    aria-label="Reset zoom"
                    onClick={() => setZoomScale(1)}
                  >
                    <IconZoomReset size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              {onDownload && (
                <Button
                  size="xs"
                  leftSection={<IconDownload size={16} />}
                  loading={downloadLoading}
                  disabled={downloadDisabled}
                  onClick={onDownload}
                >
                  {downloadLabel}
                </Button>
              )}
            </Group>
          </Group>
          <div
            ref={scrollContainerRef}
            className="rounded-md border border-gray-200 bg-gray-100 p-3"
            style={{
              overflow: 'auto',
              maxHeight: '72vh',
              textAlign: 'center',
              cursor: zoomScale > 1 ? 'grab' : 'default',
            }}
          >
            <img
              src={imageUrl}
              alt={imageAlt}
              style={{
                width: `${zoomScale * 100}%`,
                maxWidth: 'none',
                transition: 'width 0.15s ease',
                display: 'block',
                margin: '0 auto',
                borderRadius: 8,
                boxShadow: '0 16px 40px rgba(15, 23, 42, 0.12)',
              }}
              draggable={false}
            />
          </div>
        </Stack>
      )}
    </Modal>
  )
}
